<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import { db } from '$lib/api';

  // Folded from former +page.server.js: load db list on mount, perform
  // switch via api.js (no SvelteKit form actions).
  let data = { configs: [], activeIndex: 1, error: null };
  let switching = false;
  let switchingTo = null;
  let message = '';
  let isError = false;
  let isLoading = true;

  $: configs = data.configs ?? [];

  async function loadDatabases() {
    isLoading = true;
    try {
      const resp = await db.list();
      const configs = (resp.databases || []).map((d) => ({
        index: d.index,
        name: d.name,
        title: d.title,
        host: d.host
      }));
      data = {
        error: null,
        configs,
        activeIndex: resp.current?.index ?? 1
      };
    } catch (err) {
      console.error('Failed to load database list:', err);
      data = {
        error: 'Failed to connect to API',
        configs: [],
        activeIndex: 1
      };
    } finally {
      isLoading = false;
    }
  }

  onMount(() => {
    loadDatabases();
  });

  async function selectDatabase(cfg) {
    // Don't switch if already active or currently switching
    if (cfg.index === data.activeIndex || switching) return;

    switching = true;
    switchingTo = cfg.index;
    message = '';
    isError = false;

    try {
      const result = await db.switchTo({ index: cfg.index });
      if (result?.success) {
        // Navigate to studies page
        goto('/studies');
      } else {
        isError = true;
        message = result?.error || 'Failed to switch database.';
        switching = false;
        switchingTo = null;
      }
    } catch (e) {
      isError = true;
      message = e?.response?.data?.error || e?.message || 'Unexpected error.';
      switching = false;
      switchingTo = null;
    }
  }
</script>

<section class="container">
  <h1 class="mb-4 text-xl font-bold text-gray-700 dark:text-gray-200">Select Database</h1>

  {#if data.error}
    <p class="error">{data.error}</p>
  {/if}

  {#if configs.length === 0}
    <div class="p-4 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
      <p class="text-yellow-800 dark:text-yellow-200">
        No database configurations found. Add entries to .env:
      </p>
      <pre class="mt-2 p-2 bg-gray-100 dark:bg-gray-800 rounded text-xs">MONGODB_NAME_1=Demo
MONGODB_URI_1=mongodb+srv://...
MONGODB_DEFAULT=1</pre>
    </div>
  {:else}
    <div class="space-y-1">
      {#each configs as cfg}
        <button
          type="button"
          disabled={switching}
          on:click={() => selectDatabase(cfg)}
          class="db-row {cfg.index === data.activeIndex ? 'active' : ''} {switchingTo === cfg.index ? 'switching' : ''}"
        >
          <span class="name">{cfg.name}</span>
          {#if cfg.title}
            <span class="title">- {cfg.title}</span>
          {/if}
          {#if cfg.host}
            <span class="host">({cfg.host})</span>
          {/if}
          <span class="status">
            {#if switchingTo === cfg.index}
              <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            {:else if cfg.index === data.activeIndex}
              <span class="badge">Active</span>
            {/if}
          </span>
        </button>
      {/each}
    </div>
  {/if}

  {#if message}
    <p class="mt-4 p-3 rounded-lg {isError ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200' : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'}">
      {message}
    </p>
  {/if}
</section>

<style>
  .container { 
    max-width: 720px; 
    margin: 2rem auto; 
    padding: 0 1rem; 
  }
  
  .db-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.625rem 0.875rem;
    border-radius: 0.375rem;
    border: 1px solid transparent;
    background: transparent;
    text-align: left;
    cursor: pointer;
    transition: all 120ms ease;
    color: inherit;
  }
  
  .db-row:hover:not(:disabled):not(.active) {
    background: rgba(59, 130, 246, 0.1);
    border-color: rgba(59, 130, 246, 0.3);
  }
  
  .db-row.active {
    background: rgba(34, 197, 94, 0.1);
    border-color: rgba(34, 197, 94, 0.3);
    cursor: default;
  }
  
  .db-row.switching {
    opacity: 0.7;
  }
  
  .db-row:disabled {
    cursor: wait;
  }
  
  .name {
    font-weight: 600;
    color: var(--text-primary, #374151);
  }
  
  :global(.dark) .name {
    color: #e5e7eb;
  }
  
  .title {
    font-size: 0.875rem;
    color: var(--text-secondary, #6b7280);
  }
  
  :global(.dark) .title {
    color: #9ca3af;
  }
  
  .host {
    font-size: 0.75rem;
    color: var(--text-muted, #9ca3af);
  }
  
  :global(.dark) .host {
    color: #6b7280;
  }
  
  .status {
    margin-left: auto;
    display: flex;
    align-items: center;
  }
  
  .badge {
    font-size: 0.75rem;
    font-weight: 500;
    padding: 0.125rem 0.5rem;
    border-radius: 9999px;
    background: rgba(34, 197, 94, 0.2);
    color: #166534;
  }
  
  :global(.dark) .badge {
    background: rgba(34, 197, 94, 0.2);
    color: #86efac;
  }
</style>
